import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {IBasketRepository} from '@/src/core/domain/repositories'
import {BasketError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {BasketData, BasketItem} from '@/src/entities/basket/types'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

/**
 * Remove From Basket Use Case
 *
 * Orchestrates removing an item from the basket.
 * Follows Single Responsibility Principle - only handles the delete operation.
 *
 * @example
 * const result = await removeFromBasketUseCase.execute(basketItem)
 * if (result.isOk()) {
 *   // Update UI with new basket data
 * } else {
 *   // Handle error
 * }
 */
@injectable()
export class RemoveFromBasketUseCase {
  @inject(DI.IBasketRepository)
  basketRepository!: IBasketRepository

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  async execute(item: BasketItem): Promise<OperationResult<BasketData, BasketError>> {
    try {
      const basketData = await this.basketRepository.delete(item.id)
      return Result.ok(basketData)
    } catch (error) {
      this.errorReporter.capture(error instanceof Error ? error : new Error(String(error)), {item_id: item.id})
      return Result.fail(new BasketError('DELETE_FAILED', error))
    }
  }
}
